import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ToastProvider } from './components/ToastProvider';
import { FeedbackProvider } from './components/FeedbackProvider';
import { UpdateAvailableBanner } from './components/UpdateAvailableBanner';
import { useAuthStore } from './store/authStore';
import { useCallStore } from './store/callStore';
import { loadPushContextById } from './crypto/storage';
import { ensureServerSelected, getCurrentIdentityId } from './services/activeDevice';
import { wsClient } from './services/websocket';
import { handleTemporaryTrustedAccessSignal } from './services/temporaryTrustedAccessSignals';
import { isWebViewMode } from './utils/webviewBridge';
import { showToast } from './utils/toast';
import './index.css';
import './i18n';
import { t } from './i18n';

const PENDING_SERVER_ID_KEY = 'videocall.pendingServerId';
const PENDING_CALL_ACTION_KEY = 'videocall.pendingCallAction';
const PENDING_MESSAGE_NAV_KEY = 'videocall.pendingMessageNav';
const CHUNK_RECOVERY_RELOAD_KEY = 'videocall.chunkRecoveryReload';
const DEFERRED_UPDATE_RELOAD_POLL_MS = 1000;
const DEFERRED_UPDATE_RELOAD_STABLE_MS = 1500;

function isChunkLoadFailureMessage(value: unknown): boolean {
  const text = value instanceof Error
    ? `${value.name}: ${value.message}`
    : typeof value === 'string'
      ? value
      : '';
  if (!text) return false;

  return text.includes('Failed to fetch dynamically imported module')
    || text.includes('Failed to load module script')
    || text.includes('Expected a JavaScript-or-Wasm module script')
    || text.includes('Importing a module script failed');
}

function reloadForChunkRecovery(): void {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(CHUNK_RECOVERY_RELOAD_KEY);
    const last = raw ? Number.parseInt(raw, 10) : 0;
    if (Number.isFinite(last) && last > 0 && now - last < 15_000) {
      return;
    }
    sessionStorage.setItem(CHUNK_RECOVERY_RELOAD_KEY, String(now));
  } catch {
    // ignore
  }

  window.location.reload();
}

function installChunkRecoveryReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    const payload = event.payload;
    if (!isChunkLoadFailureMessage(payload)) return;
    event.preventDefault();
    reloadForChunkRecovery();
  });

  window.addEventListener('error', (event) => {
    const target = event.target;
    if (target instanceof HTMLScriptElement && target.type === 'module') {
      reloadForChunkRecovery();
      return;
    }

    if (isChunkLoadFailureMessage(event.message) || isChunkLoadFailureMessage(event.error)) {
      reloadForChunkRecovery();
    }
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkLoadFailureMessage(event.reason)) return;
    event.preventDefault();
    reloadForChunkRecovery();
  });
}

installChunkRecoveryReload();

function isUnspecifiedServerId(serverId: string | null | undefined): boolean {
  const normalized = (serverId || '').toString().trim().toLowerCase();
  return normalized === '' || normalized === 'default' || normalized === 'default-server' || normalized === 'unknown';
}

async function resolveSwitchTargetFromIncoming(serverOrigin?: string, serverId?: string, pushId?: string): Promise<string | null> {
  const directOrigin = (serverOrigin || '').toString().trim();
  if (!isUnspecifiedServerId(directOrigin)) {
    return directOrigin;
  }

  const direct = (serverId || '').toString().trim();
  if (!isUnspecifiedServerId(direct)) {
    return direct;
  }

  const normalizedPushId = (pushId || '').toString().trim();
  if (!normalizedPushId) return null;

  try {
    const ctx = await loadPushContextById(normalizedPushId);
    if (!ctx) return null;

    const contextServerOrigin = (ctx.serverOrigin || '').toString().trim();
    if (!isUnspecifiedServerId(contextServerOrigin)) {
      return contextServerOrigin;
    }

    const contextServerId = (ctx.serverId || '').toString().trim();
    if (!isUnspecifiedServerId(contextServerId)) {
      return contextServerId;
    }

    const contextServerUrl = (ctx.serverUrl || '').toString().trim();
    if (contextServerUrl) return contextServerUrl;
  } catch {
    // ignore
  }

  return null;
}

function trySwitchToServerId(serverId: string): boolean {
  return ensureServerSelected(serverId);
}

function rememberAndTrySwitch(serverId: string) {
  try {
    sessionStorage.setItem(PENDING_SERVER_ID_KEY, serverId);
  } catch {
    // ignore
  }

  // Best-effort immediate attempt.
  if (trySwitchToServerId(serverId)) {
    try {
      sessionStorage.removeItem(PENDING_SERVER_ID_KEY);
    } catch {
      // ignore
    }
  }
}

type PendingCallAction = {
  callSessionId: string;
  action: 'answer' | 'reject';
  serverId?: string;
  at: number;
};

type PendingMessageNav = {
  peerId?: string;
  chatId?: string;
  at: number;
};

function rememberPendingCallAction(action: PendingCallAction) {
  try {
    const existingRaw = sessionStorage.getItem(PENDING_CALL_ACTION_KEY);
    if (existingRaw) {
      const existing = JSON.parse(existingRaw) as Partial<PendingCallAction>;
      const sameAction = existing.callSessionId === action.callSessionId
        && existing.action === action.action
        && existing.serverId === action.serverId;
      const existingAt = Number(existing.at || 0);
      if (sameAction && Number.isFinite(existingAt) && Math.abs(action.at - existingAt) < 10_000) {
        return;
      }
    }
    sessionStorage.setItem(PENDING_CALL_ACTION_KEY, JSON.stringify(action));
  } catch {
    // ignore
  }

  window.dispatchEvent(
    new CustomEvent('videocall:callAction', {
      detail: action
    })
  );
}

function hasPendingCallAction(): boolean {
  try {
    return !!sessionStorage.getItem(PENDING_CALL_ACTION_KEY);
  } catch {
    return false;
  }
}

function isCallCriticalNow(): boolean {
  const { activeCall, incomingCall } = useCallStore.getState();
  return !!activeCall
    || !!incomingCall
    || window.location.pathname === '/call'
    || window.location.pathname.startsWith('/call/')
    || hasPendingCallAction()
    || wsClient.isExternalMode();
}

function applyPendingMessageNavigation(): boolean {
  try {
    const raw = sessionStorage.getItem(PENDING_MESSAGE_NAV_KEY);
    if (!raw) return false;

    const parsed = JSON.parse(raw) as PendingMessageNav;
    const localIdentityId = getCurrentIdentityId();
    if (!localIdentityId) return false;

    let targetPath = '/messages';
    const peerId = (parsed.peerId || '').trim();
    const chatId = (parsed.chatId || '').trim();

    if (chatId) {
      targetPath = `/messages/group/${encodeURIComponent(localIdentityId)}/${encodeURIComponent(chatId)}`;
    } else if (peerId) {
      targetPath = `/messages/${encodeURIComponent(localIdentityId)}/${encodeURIComponent(peerId)}`;
    }

    try {
      window.history.replaceState({}, '', targetPath);
    } catch {
      window.location.href = targetPath;
      return true;
    }

    sessionStorage.removeItem(PENDING_MESSAGE_NAV_KEY);
    return true;
  } catch {
    return false;
  }
}

const SW_UPDATED_FLAG = 'app_sw_updated';

// Service workers tend to break Vite HMR in dev (stale caches, WS issues),
// so only register in production. In dev, proactively unregister any
// previously-installed SW to avoid confusing local behavior.
if ('serviceWorker' in navigator) {
  if (isWebViewMode) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.getRegistrations()
        .then((registrations) => {
          registrations.forEach((registration) => {
            void registration.unregister();
          });
        })
        .catch(() => {
          // Native Android owns WebView shell updates.
        });
    });
  }

  if (import.meta.env.DEV) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => registration.unregister());
    });
  }

  if (import.meta.env.PROD && !isWebViewMode) {
    window.addEventListener('load', () => {
      const hadControllerAtStart = !!navigator.serviceWorker.controller;
      let reloadRequested = false;
      let reloadQueuedForAfterCall = false;
      let updateBannerShown = false;
      let deferredReloadPollTimer: number | null = null;
      let deferredReloadReadyTimer: number | null = null;

      const clearDeferredReloadTimers = () => {
        if (deferredReloadPollTimer !== null) {
          window.clearTimeout(deferredReloadPollTimer);
          deferredReloadPollTimer = null;
        }
        if (deferredReloadReadyTimer !== null) {
          window.clearTimeout(deferredReloadReadyTimer);
          deferredReloadReadyTimer = null;
        }
      };

      const executeReload = (registration: ServiceWorkerRegistration): 'reloading' => {
        reloadRequested = true;
        reloadQueuedForAfterCall = false;
        clearDeferredReloadTimers();

        try {
          registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
        } catch {
          // ignore
        }

        window.setTimeout(() => {
          if (reloadRequested) {
            try { localStorage.setItem(SW_UPDATED_FLAG, '1'); } catch { /* ignore */ }
            window.location.reload();
          }
        }, 5000);

        return 'reloading';
      };

      const scheduleReloadAfterCall = (registration: ServiceWorkerRegistration) => {
        if (deferredReloadPollTimer !== null || deferredReloadReadyTimer !== null) return;

        const poll = () => {
          deferredReloadPollTimer = null;
          if (!reloadQueuedForAfterCall || reloadRequested) return;

          if (isCallCriticalNow()) {
            deferredReloadPollTimer = window.setTimeout(poll, DEFERRED_UPDATE_RELOAD_POLL_MS);
            return;
          }

          deferredReloadReadyTimer = window.setTimeout(() => {
            deferredReloadReadyTimer = null;
            if (!reloadQueuedForAfterCall || reloadRequested) return;

            if (isCallCriticalNow()) {
              deferredReloadPollTimer = window.setTimeout(poll, DEFERRED_UPDATE_RELOAD_POLL_MS);
              return;
            }

            executeReload(registration);
          }, DEFERRED_UPDATE_RELOAD_STABLE_MS);
        };

        deferredReloadPollTimer = window.setTimeout(poll, DEFERRED_UPDATE_RELOAD_POLL_MS);
      };

      const requestReload = (registration: ServiceWorkerRegistration): 'queued' | 'reloading' => {
        if (isCallCriticalNow()) {
          reloadQueuedForAfterCall = true;
          scheduleReloadAfterCall(registration);
          return 'queued';
        }
        return executeReload(registration);
      };

      const showUpdateBanner = (registration: ServiceWorkerRegistration) => {
        if (updateBannerShown) return;
        updateBannerShown = true;

        if (isWebViewMode) {
          // In WebView mode: auto-reload instead of showing a toast.
          // respects the same deferred-until-call-ends logic.
          requestReload(registration);
          return;
        }

        window.dispatchEvent(
          new CustomEvent('videocall:updateAvailable', {
            detail: {
              requestReload: () => requestReload(registration)
            }
          })
        );
      };

      // When the updated SW takes control, only reload if the user explicitly accepted.
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!hadControllerAtStart) return;
        if (!reloadRequested) return;
        try { localStorage.setItem(SW_UPDATED_FLAG, '1'); } catch { /* ignore */ }
        window.location.reload();
      });

      // Cache-bust the SW script URL per build.
      const swUrl = `/sw.js?v=${encodeURIComponent(__BUILD_ID__)}`;

      navigator.serviceWorker
        .register(swUrl, { updateViaCache: 'none' })
        .then((registration) => {
          // If an update was already installed and is waiting, surface it.
          if (registration.waiting && navigator.serviceWorker.controller) {
            showUpdateBanner(registration);
          }

          registration.addEventListener('updatefound', () => {
            const worker = registration.installing;
            if (!worker) return;
            worker.addEventListener('statechange', () => {
              // New SW finished installing; if there's an existing controller, it's an update.
              if (worker.state === 'installed' && navigator.serviceWorker.controller) {
                showUpdateBanner(registration);
              }
            });
          });

          // Best-effort: prompt an update check on each page load.
          try {
            void registration.update();
          } catch {
            // ignore
          }

          // Periodically re-check for updates while the page stays open (e.g. long-lived WebView).
          window.setInterval(() => {
            try { void registration.update(); } catch { /* ignore */ }
          }, 60 * 60 * 1000); // every hour
        })
        .catch((error) => {
          console.error('Service worker registration failed:', error);
        });
    });
  }

  // Handle messages from the service worker (push notification clicks, etc.)
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data as any;
    if (!data || typeof data !== 'object') return;

    void (async () => {
      // Generic navigation request (e.g. message notification click).
      if (data.type === 'open_url' && typeof data.url === 'string' && data.url) {
        try {
          window.location.href = data.url;
        } catch {
          // ignore
        }
        return;
      }

      if (data.type === 'temporary_trusted_access_request') {
        handleTemporaryTrustedAccessSignal({
          requestId: data.requestId,
          identityId: data.identityId,
          temporaryDeviceId: data.temporaryDeviceId,
          source: 'push'
        });
        return;
      }

      // Incoming-call push notifications include { serverOrigin?, serverId?, pushId, callSessionId }.
      // Switch active identity so wsClient reconnects to the correct server.
      const serverOrigin = (data.serverOrigin || '').toString();
      const serverId = (data.serverId || '').toString();
      const pushId = (data.pushId || '').toString();
      const switchTarget = await resolveSwitchTargetFromIncoming(serverOrigin, serverId, pushId);
      if (switchTarget) {
        rememberAndTrySwitch(switchTarget);
      }

      // Handle notification action buttons (Answer/Reject)
      const callSessionId = (data.callSessionId || '').toString();
      const type = (data.type || '').toString();
      const action = (data.action || '').toString();

      if (callSessionId) {
        // Backwards compatible with older SW message types.
        const resolvedAction =
          (type === 'answer_call' ? 'answer' : type === 'reject_call' ? 'reject' : null) ||
          (type === 'incoming_call_action' && (action === 'answer' || action === 'reject') ? action : null);

        if (resolvedAction) {
          rememberPendingCallAction({
            callSessionId,
            action: resolvedAction,
            serverId: switchTarget || undefined,
            at: Date.now()
          });
        }
      }
    })();
  });
}

// If the service worker opened the app with query params, handle them as well.
try {
  const params = new URLSearchParams(window.location.search);
  const serverOrigin = params.get('serverOrigin');
  const serverId = params.get('serverId');
  const pushId = params.get('pushId');
  void resolveSwitchTargetFromIncoming(serverOrigin || undefined, serverId || undefined, pushId || undefined).then((switchTarget) => {
    if (switchTarget) {
      rememberAndTrySwitch(switchTarget);
    }
  });

  const callSessionId = params.get('call');
  const peerId = params.get('peerId');
  const chatId = params.get('chatId');
  const callAction = params.get('callAction');
  const action = callAction === 'answer' || callAction === 'reject' ? callAction : null;

  if (callSessionId && action) {
    rememberPendingCallAction({
      callSessionId,
      action,
      serverId: serverOrigin || serverId || undefined,
      at: Date.now()
    });
  }

  if (!callSessionId && (peerId || chatId)) {
    try {
      sessionStorage.setItem(
        PENDING_MESSAGE_NAV_KEY,
        JSON.stringify({
          peerId: peerId || undefined,
          chatId: chatId || undefined,
          at: Date.now()
        } satisfies PendingMessageNav)
      );
    } catch {
      // ignore
    }
  }

  // If we were opened from a call notification, show the calls screen by default.
  // The actual incoming call UI will appear once WS delivers call:incoming (or pending replay after register).
  if (callSessionId && !action && window.location.pathname === '/') {
    try {
      window.history.replaceState({}, '', '/calls');
    } catch {
      // ignore
    }
  }
} catch {
  // ignore
}

// If we received a SW message before identities/devices were hydrated, retry after init.
useAuthStore.subscribe(() => {
  let pending: string | null = null;
  try {
    pending = sessionStorage.getItem(PENDING_SERVER_ID_KEY);
  } catch {
    pending = null;
  }
  if (!pending) return;

  if (trySwitchToServerId(pending)) {
    try {
      sessionStorage.removeItem(PENDING_SERVER_ID_KEY);
    } catch {
      // ignore
    }
  }

  void applyPendingMessageNavigation();
});

try {
  void applyPendingMessageNavigation();
} catch {
  // ignore
}

// Prevent pinch-to-zoom on mobile devices
document.addEventListener('gesturestart', (e) => e.preventDefault());
document.addEventListener('gesturechange', (e) => e.preventDefault());
document.addEventListener('gestureend', (e) => e.preventDefault());

// Prevent double-tap zoom
let lastTouchEnd = 0;
document.addEventListener('touchend', (e) => {
  const now = Date.now();
  if (now - lastTouchEnd <= 300) {
    e.preventDefault();
  }
  lastTouchEnd = now;
}, false);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <ToastProvider>
        <FeedbackProvider>
          <UpdateAvailableBanner />
          <App />
        </FeedbackProvider>
      </ToastProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// Show a toast if the app was just updated via service worker.
try {
  if (localStorage.getItem(SW_UPDATED_FLAG)) {
    localStorage.removeItem(SW_UPDATED_FLAG);
    // Delay slightly so ToastProvider has time to mount and subscribe to the event.
    window.setTimeout(() => {
      showToast({ message: t('update.updated'), variant: 'success' });
    }, 1500);
  }
} catch {
  // ignore
}
